import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getToken } from '../shared/auth';

// 이벤트 카테고리 상수
export const EVENT_CATEGORIES = {
  FAMILY: 'family',
  BIRTHDAY: 'birthday',
  TRAVEL: 'travel',
  HEALTH: 'health',
  WORK: 'work',
  OTHER: 'other'
};

// 카테고리별 색상
const CATEGORY_COLORS = {
  [EVENT_CATEGORIES.FAMILY]: '#0071e3',
  [EVENT_CATEGORIES.BIRTHDAY]: '#ff9500',
  [EVENT_CATEGORIES.TRAVEL]: '#34c759',
  [EVENT_CATEGORIES.HEALTH]: '#ff3b30',
  [EVENT_CATEGORIES.WORK]: '#5856d6',
  [EVENT_CATEGORIES.OTHER]: '#86868b'
};

const GOOGLE_EVENT_COLOR = '#4285f4';

const CalendarContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';

export const CalendarProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  // Google Calendar 관련 상태
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [googleEvents, setGoogleEvents] = useState([]);

  // Google 연결 상태 확인
  const checkGoogleConnection = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setGoogleConnected(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/calendar/auth/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setGoogleConnected(data.connected);
    } catch (error) {
      console.error('Failed to check Google connection:', error);
      setGoogleConnected(false);
    }
  }, []);

  // Google Calendar 연결
  const connectGoogle = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setGoogleError('로그인이 필요합니다.');
      return;
    }

    setGoogleLoading(true);
    setGoogleError(null);

    try {
      const response = await fetch(`${API_URL}/api/calendar/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        setGoogleError('인증 URL을 가져올 수 없습니다.');
      }
    } catch (error) {
      console.error('Failed to connect Google:', error);
      setGoogleError('Google 연결에 실패했습니다.');
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  // Google Calendar 연결 해제
  const disconnectGoogle = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    setGoogleLoading(true);
    try {
      await fetch(`${API_URL}/api/calendar/auth/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleConnected(false);
      setGoogleEvents([]);
    } catch (error) {
      console.error('Failed to disconnect Google:', error);
      setGoogleError('연결 해제에 실패했습니다.');
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  // Google Calendar 이벤트 가져오기
  const fetchGoogleEvents = useCallback(async () => {
    const token = getToken();
    if (!token || !googleConnected) return;

    setGoogleLoading(true);
    try {
      const now = new Date();
      const timeMin = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const timeMax = new Date(now.getFullYear(), now.getMonth() + 3, 0).toISOString();

      const response = await fetch(
        `${API_URL}/api/calendar/events?timeMin=${timeMin}&timeMax=${timeMax}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setGoogleEvents(data.events || []);
      } else if (response.status === 401) {
        setGoogleConnected(false);
        setGoogleError('Google 인증이 만료되었습니다. 다시 연결해주세요.');
      }
    } catch (error) {
      console.error('Failed to fetch Google events:', error);
    } finally {
      setGoogleLoading(false);
    }
  }, [googleConnected]);

  // Google 이벤트 새로고침
  const refreshGoogleEvents = useCallback(() => {
    if (googleConnected) {
      fetchGoogleEvents();
    }
  }, [googleConnected, fetchGoogleEvents]);

  // 초기 로드
  useEffect(() => {
    checkGoogleConnection();
  }, [checkGoogleConnection]);

  // Google 연결 시 이벤트 가져오기
  useEffect(() => {
    if (googleConnected) {
      fetchGoogleEvents();
    }
  }, [googleConnected, fetchGoogleEvents]);

  // URL에서 연결 결과 확인
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('connected') === 'true') {
      setGoogleConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('error')) {
      setGoogleError('Google 연결에 실패했습니다: ' + urlParams.get('error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // 모든 이벤트 (로컬 + Google)
  const allEvents = [...events, ...googleEvents];

  // 이월/다음달 이동
  const goToPreviousMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(today);
  }, []);

  // 날짜별 이벤트 조회
  const getEventsForDate = useCallback((date) => {
    const dateStr = date.toISOString().split('T')[0];
    return allEvents.filter(event => event.date === dateStr);
  }, [allEvents]);

  // 월별 이벤트 조회
  const getEventsForMonth = useCallback((year, month) => {
    return allEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getFullYear() === year && eventDate.getMonth() === month;
    });
  }, [allEvents]);

  // 다가오는 이벤트
  const getUpcomingEvents = useCallback((days = 7) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + days);

    return allEvents
      .filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= today && eventDate <= endDate;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [allEvents]);

  // 이벤트 추가
  const addEvent = useCallback(async (eventData, syncToGoogle = false) => {
    const newEvent = {
      id: `local_${Date.now()}`,
      ...eventData,
      createdAt: new Date().toISOString()
    };

    setEvents(prev => [...prev, newEvent]);

    // Google 동기화
    if (syncToGoogle && googleConnected) {
      const token = getToken();
      try {
        await fetch(`${API_URL}/api/calendar/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(eventData)
        });
        refreshGoogleEvents();
      } catch (error) {
        console.error('Failed to sync to Google:', error);
      }
    }

    return newEvent;
  }, [googleConnected, refreshGoogleEvents]);

  // 이벤트 수정
  const updateEvent = useCallback(async (eventId, updates) => {
    // Google 이벤트인 경우
    if (eventId.startsWith('google_')) {
      const googleId = eventId.replace('google_', '');
      const token = getToken();
      try {
        await fetch(`${API_URL}/api/calendar/events/${googleId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(updates)
        });
        refreshGoogleEvents();
      } catch (error) {
        console.error('Failed to update Google event:', error);
      }
      return;
    }

    // 로컬 이벤트
    setEvents(prev =>
      prev.map(event =>
        event.id === eventId ? { ...event, ...updates } : event
      )
    );
  }, [refreshGoogleEvents]);

  // 이벤트 삭제
  const deleteEvent = useCallback(async (eventId) => {
    // Google 이벤트인 경우
    if (eventId.startsWith('google_')) {
      const googleId = eventId.replace('google_', '');
      const token = getToken();
      try {
        await fetch(`${API_URL}/api/calendar/events/${googleId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        refreshGoogleEvents();
      } catch (error) {
        console.error('Failed to delete Google event:', error);
      }
      return;
    }

    // 로컬 이벤트
    setEvents(prev => prev.filter(event => event.id !== eventId));
  }, [refreshGoogleEvents]);

  // 카테고리 색상 조회
  const getCategoryColor = useCallback((category, isGoogleEvent = false) => {
    if (isGoogleEvent) return GOOGLE_EVENT_COLOR;
    return CATEGORY_COLORS[category] || CATEGORY_COLORS[EVENT_CATEGORIES.OTHER];
  }, []);

  // 이벤트 검색
  const searchEvents = useCallback((query) => {
    const lowerQuery = query.toLowerCase();
    return allEvents.filter(event =>
      event.title.toLowerCase().includes(lowerQuery) ||
      (event.description && event.description.toLowerCase().includes(lowerQuery))
    );
  }, [allEvents]);

  const value = {
    events: allEvents,
    currentMonth,
    setCurrentMonth,
    selectedDate,
    setSelectedDate,
    goToPreviousMonth,
    goToNextMonth,
    goToToday,
    getEventsForDate,
    getEventsForMonth,
    getUpcomingEvents,
    addEvent,
    updateEvent,
    deleteEvent,
    getCategoryColor,
    searchEvents,
    EVENT_CATEGORIES,
    // Google Calendar
    googleConnected,
    googleLoading,
    googleError,
    connectGoogle,
    disconnectGoogle,
    refreshGoogleEvents
  };

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};

export default CalendarContext;
